import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FunctionalLocationOverview } from '../types/funcloc.types';
import { LoadingService } from './loading.service';
import { DataService } from './data.service';
import { ApiResponse } from '../types/general.types';
import { SelectedSite } from '../types/selectedSite.types';
import { AuthService } from './auth.service';
import { SelectedContentService } from './selectedcontent.service';
import { SelectedsystemService } from './selectedsystem.service';
import { SubscriptionService } from './subscription.service';
import { ServiceMetricsOverviewSite } from '../types/servicemetrics.types';
import { Title } from '@angular/platform-browser';
import { ServiceHistoryOverviewSite } from '../types/servicehistory.types';
import { environment } from 'src/environments/environment';
import { BusinessPartnerAccessService } from './business-partner-access.service';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
    providedIn: 'root'
})
export class SelectedSiteService {

    constructor(
        private loadingService: LoadingService, 
        private dataService: DataService, 
        private authService: AuthService, 
        private selectedContentService: SelectedContentService, 
        private subscriptionService: SubscriptionService, 
        private selectedsystemService: SelectedsystemService,
        private businessPartnerAccessService: BusinessPartnerAccessService,
        private title: Title,
        private translocoService: TranslocoService
    ) { }

    //Reload Selected System
    $reloadSelectedSite: Subject<void> = new Subject<void>();

    //Overview Data
    $siteoverview: BehaviorSubject<FunctionalLocationOverview> = new BehaviorSubject<FunctionalLocationOverview>(null);
    $servicehistory: BehaviorSubject<ServiceHistoryOverviewSite> = new BehaviorSubject<ServiceHistoryOverviewSite>(null);
    $servicemetrics: BehaviorSubject<ServiceMetricsOverviewSite> = new BehaviorSubject<ServiceMetricsOverviewSite>(null);

    //Distinct Product Families derived from siteoverview
    $productFamilies: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    //Details Data
    //Contained in selectedSystemService as they share data

    //Prompts
    $prompts: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    private get shouldLoadServiceMetrics(): boolean {
        return environment.enableServiceMetricsWidget;
    }

    //Used to reset widgets for a re-load
    resetForReload(): void {

        //Reset all saved data
        this.$siteoverview.next(null);
        this.$servicemetrics.next(null);
        this.$servicehistory.next(null);
        this.$prompts.next([]);
        this.$productFamilies.next([]);
        this.selectedsystemService.$upcomingservices.next(null);
        this.selectedsystemService.$upcomingservicedetails.next(null);

        //Reset Loading States for Widgets
        this.loadingService.$isSiteLoadingSite.next(true);
        this.loadingService.$isServiceMetricsLoadingSite.next(this.shouldLoadServiceMetrics);
        this.loadingService.$isServiceHistoryLoadingSite.next(true);
        this.loadingService.$isPromptsSiteLoading.next(true);
        this.loadingService.$isUpcomingServicesLoading.next(true);
        this.loadingService.$isUpcomingServiceDetailsLoading.next(true);

        //Reset Error and Empty for Widgets
        this.loadingService.$isSiteErrorSite.next(false);
        this.loadingService.$isServiceMetricsErrorSite.next(false);
        this.loadingService.$isServiceHistoryErrorSite.next(false);
        this.loadingService.$isPromptsSiteError.next(false);

        //Reset Empty
        this.loadingService.$isSiteEmptySite.next(false);
        this.loadingService.$isServiceMetricsEmptySite.next(false);
        this.loadingService.$isServiceHistoryEmptySite.next(false);
        this.loadingService.$isUpcomingServicesError.next(false);
        this.loadingService.$isUpcomingServicesEmpty.next(false);
        this.loadingService.$isUpcomingServiceDetailsError.next(false);
        this.loadingService.$isUpcomingServiceDetailsEmpty.next(false);

    }

    resetAllData() {

        //Reset UI selections
        this.selectedsystemService.$selectedPattern.next(null);
        this.selectedsystemService.$selectedNotification.next(null);

        //Reset Main State
        this.$siteoverview.next(null);
        this.$servicehistory.next(null);
        this.$servicemetrics.next(null);
        this.$prompts.next([]);
        this.$productFamilies.next([]);
        this.selectedsystemService.$upcomingservices.next(null);
        this.selectedsystemService.$upcomingservicedetails.next(null);

        //Reset Details

        this.selectedsystemService.$notifications.next(null);
        this.selectedsystemService.$spareparts.next(null);
        this.selectedsystemService.$visits.next(null);
        this.selectedsystemService.$proas.next(null);

        //Reset Sub-Detail States
        this.selectedsystemService.$proaDetails.next(null);
        this.selectedsystemService.$notificationDetails.next(null);
        this.selectedsystemService.$notificationSummary.next(null);

        //Reset Cache
        this.selectedsystemService.proasPullDate = null;
        this.selectedsystemService.proasPulledFor = null;
        this.selectedsystemService.notiPulledFor = null;
        this.selectedsystemService.NotiPullDate = null;
        this.selectedsystemService.partsPulledFor = null;
        this.selectedsystemService.PartsPullDate = null;
        this.selectedsystemService.visitsPulledFor = null;
        this.selectedsystemService.VisitsPullDate = null;


    }

    pullData(site: string): void {

        //1) Header for Customer Site Overview
        this.dataService.getFunctionalLocationOverview(site).subscribe({
            next: ({ response, status }: ApiResponse<any>) => {
                if (status === 204) {
                    this.loadingService.$isSiteEmptySite.next(true);
                }
                else if (status === 200) {
                    this.$siteoverview.next(response);
                    // Extract distinct product families from device_data
                    const productFamilies = [...new Set(
                        (response?.device_data || [])
                            .map((device: any) => device.product_family)
                            .filter((pf: string) => pf != null)
                    )];
                    this.$productFamilies.next(productFamilies as string[]);
                }
                this.loadingService.$isSiteLoadingSite.next(false);
            },
            error: (error) => {
                this.loadingService.$isSiteErrorSite.next(true);
                this.loadingService.$isSiteLoading.next(false);
            }
        });

        //2) Service Metrics Overview for Site
        if (this.shouldLoadServiceMetrics) {
            this.dataService.getServiceMetricsOverviewSite(site).subscribe({
                next: ({ response, status }: ApiResponse<ServiceMetricsOverviewSite>) => {
                    if (status === 204) {
                        this.loadingService.$isServiceMetricsEmptySite.next(true);
                    }
                    else if (status === 200) {
                        this.$servicemetrics.next(response);
                    }
                    this.loadingService.$isServiceMetricsLoadingSite.next(false);
                },
                error: (error) => {
                    this.loadingService.$isServiceMetricsErrorSite.next(true);
                    this.loadingService.$isServiceMetricsLoadingSite.next(false);
                }
            });
        } else {
            this.$servicemetrics.next(null);
            this.loadingService.$isServiceMetricsLoadingSite.next(false);
            this.loadingService.$isServiceMetricsErrorSite.next(false);
            this.loadingService.$isServiceMetricsEmptySite.next(false);
        }

        //3) Service History Overview for Site
        if((this.businessPartnerAccessService.canViewServiceData) || !environment.businessPartnerMode) {
        this.dataService.getServiceHistoryOverviewSite(site).subscribe({
            next: ({ response, status }: ApiResponse<any>) => {
                if (status === 204) {
                    this.loadingService.$isServiceHistoryEmptySite.next(true);
                }
                else if (status === 200) {
                    this.$servicehistory.next(response);
                }
                this.loadingService.$isServiceHistoryLoadingSite.next(false);
            },
            error: (error) => {
                this.loadingService.$isServiceHistoryErrorSite.next(true);
                this.loadingService.$isServiceHistoryLoadingSite.next(false);
            }
        });
        }

        //4) Prompts for Site
        this.dataService.getPromptsSite(site).subscribe({
            next: (data: any) => {
                this.$prompts.next(data?.response?.prompts || []);
                this.loadingService.$isPromptsSiteLoading.next(false);
            },
            error: (error) => {
                console.error("Error fetching Prompts information:", error);
                this.loadingService.$isPromptsSiteError.next(true);
            }
        });
        // Pull upcoming services for site
        if(this.businessPartnerAccessService.canViewServiceData) {
        this.dataService.getUpcomingServicesSiteOverview(site).subscribe({
            next: ({ response, status }: ApiResponse<any>) => {
                if (status === 204) {
                    this.loadingService.$isUpcomingServicesEmpty.next(true);
                }
                else if (status === 200) {
                    this.selectedsystemService.$upcomingservices.next(response);
                }
                this.loadingService.$isUpcomingServicesLoading.next(false);
            },
            error: (error) => {
                this.loadingService.$isUpcomingServicesError.next(true);
                this.loadingService.$isUpcomingServicesLoading.next(false);
            }
        });
    }
        
    
    if(this.businessPartnerAccessService.canViewServiceData || !environment.businessPartnerMode) {
        this.dataService.getUpcomingServicesSiteDetails(site).subscribe({
            next: ({ response, status }: ApiResponse<any>) => {
                if (status === 204) {
                    this.loadingService.$isUpcomingServiceDetailsEmpty.next(true);
                }
                else if (status === 200) {
                    this.selectedsystemService.$upcomingservicedetails.next(response);
                }
                this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
            },
            error: (error) => {
                this.loadingService.$isUpcomingServiceDetailsError.next(true);
                this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
            }
        });

    }
}

    /**
     * Loads site details + access check and selects the site.
     * Emits the newly-selected site (or null if no selection happened).
     * UI side-effects (blur, clearing inputs, widget resets, etc.) should be done by the caller.
     */
    setNewSite(siteId: string): Observable<SelectedSite | null> {

        if (siteId === this.selectedContentService.currentSite?.Id) {
            return of(null);
        }

        this.selectedContentService.clear();
        this.resetForReload();
        this.selectedContentService.$isLoadingContent.next(true);

        return forkJoin([
            this.dataService.searchExactSite(siteId),
            this.dataService.accessSite({
                "email_id": this.authService.userData?.basic["mail"],
                "func_loc": siteId
            })
        ]).pipe(
            map(([exactSiteResult, siteDeviceResult]) => {
                const { response } = exactSiteResult;

                if (siteDeviceResult.response["support"] === 'None') {
                    const entry = {
                        id: "unsupported-site" + crypto.randomUUID(),
                        type: "warning",
                        title: this.translocoService.translate('home.notification.unsupportedSite.title'),
                        message: this.translocoService.translate('home.notification.unsupportedSite.message'),
                        timestamp: new Date().toISOString(),
                        read: false
                    };

                    const current = this.subscriptionService.$notifications.getValue();
                    const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

                    this.subscriptionService.$notifications.next([...current, entry]);
                    this.subscriptionService.$notificationsCenter.next([...currentCenter, entry]);

                    //Stop Content Loading
                    this.selectedContentService.$isLoadingContent.next(false);

                    return null;
                }

                if (siteDeviceResult.response["support"] === 'Unauthorized') {
                    const entry = {
                        id: "unauthorized-site" + crypto.randomUUID(),
                        type: "warning",
                        title: this.translocoService.translate('home.notification.unauthorizedSite.title'),
                        message: this.translocoService.translate('home.notification.unauthorizedSite.message'),
                        timestamp: new Date().toISOString(),
                        read: false
                    };

                    const current = this.subscriptionService.$notifications.getValue();
                    const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

                    this.subscriptionService.$notifications.next([...current, entry]);
                    this.subscriptionService.$notificationsCenter.next([...currentCenter, entry]);

                    //Stop Content Loading
                    this.selectedContentService.$isLoadingContent.next(false);

                    return null;
                }

                const newSite: SelectedSite = {
                    'Id': response.is_current ? response.id : response.new_id,
                    'Name': response.location_name,
                    'SupportType': response.support
                };

                this.selectedContentService.selectSite(newSite);

                //Change Browswer Title
                this.title.setTitle(newSite.Name + ' - AXIS');

                return newSite;
            }),
            catchError(() => {
                //Stop Content Loading
                this.selectedContentService.$isLoadingContent.next(false);
                return of(null);
            })
        );
    }
}